import type {Scene} from '@babylonjs/core/scene';
import type {AbstractMesh} from '@babylonjs/core/Meshes/abstractMesh';
import {MeshBuilder} from '@babylonjs/core/Meshes/meshBuilder';
import {TransformNode} from '@babylonjs/core/Meshes/transformNode';
import {PBRMaterial} from '@babylonjs/core/Materials/PBR/pbrMaterial';
import {Color3} from '@babylonjs/core/Maths/math.color';
import type {ShadowGenerator} from '@babylonjs/core/Lights/Shadows/shadowGenerator';
import {getAssetManager,type InstantiatedModel} from '../../systems/AssetManager';
import {CLOCKWORK_CANOPY_PLATFORMS,platformTop} from '../layouts';
import {freezeStaticMeshes} from '../staticOptimization';
import {tagAs} from '../PlaceholderTag';
import layout from './canopyShadowShelterLayout.json';

const feet=layout.shelters.flatMap(s=>s.feet).map(f=>({...f,
  y:platformTop(CLOCKWORK_CANOPY_PLATFORMS.platforms.find(p=>p.name===f.platform)!)}));
const required=['cloth','wood','frame','fasteners'].map(n=>'CanopyShade_'+n)
  .concat(feet.map((_,i)=>'CanopyShade_foot_'+i));

/** Rear astronomical shade cloth. Owns only art, never stealth volumes/physics. */
export class CanopyShadowShelter {
  readonly ready:Promise<boolean>;
  private readonly fallbackRoot:TransformNode;
  private readonly fallback:AbstractMesh[]=[];
  private readonly materials:PBRMaterial[]=[];
  private source:InstantiatedModel|null=null;
  private casters:AbstractMesh[]=[];
  private retired=false;
  private readonly observer;

  constructor(private readonly scene:Scene,private readonly shadows:ShadowGenerator) {
    this.fallbackRoot=new TransformNode('canopyShadeFallback',scene);
    this.createFallback();
    this.casters=[...this.fallback];
    for(const mesh of this.casters)shadows.addShadowCaster(mesh,false);
    this.observer=scene.onDisposeObservable.addOnce(()=>this.dispose());
    this.ready=this.load();
  }

  private material(name:string,color:Color3,roughness:number,metallic=0):PBRMaterial {
    const m=new PBRMaterial('canopyShadeFallback_'+name,this.scene);
    m.albedoColor=color;m.roughness=roughness;m.metallic=metallic;
    this.materials.push(m);return m;
  }

  private createFallback():void {
    const cloth=this.material('cloth',new Color3(.14,.10,.17),.96);
    const wood=this.material('wood',new Color3(.15,.11,.08),.9);
    const iron=this.material('iron',new Color3(.055,.065,.06),.75,.5);
    const finish=(mesh:AbstractMesh,material:PBRMaterial):AbstractMesh=>{
      mesh.parent=this.fallbackRoot;mesh.material=material;mesh.isPickable=false;
      mesh.receiveShadows=true;tagAs(mesh,'placeholder');this.fallback.push(mesh);return mesh;
    };
    const box=(name:string,p:number[],size:number[],material:PBRMaterial)=>{
      const m=MeshBuilder.CreateBox('canopyShadeFallback_'+name,
        {width:size[0],height:size[1],depth:size[2]},this.scene);
      m.position.set(p[0],p[1],p[2]);return finish(m,material);
    };
    feet.forEach((f,i)=>{
      box('foot_'+i,[f.x,f.y+layout.footSize[1]/2,layout.postZ],layout.footSize,iron);
      const top=layout.shelters.find(s=>s.feet.some(p=>p.x===f.x))!.railY;
      const bottom=f.y+layout.footSize[1];
      const post=MeshBuilder.CreateCylinder('canopyShadeFallback_post_'+i,
        {height:top-bottom,diameter:.11,tessellation:8},this.scene);
      post.position.set(f.x,(top+bottom)/2,layout.postZ);finish(post,iron);
    });
    for(const s of layout.shelters){
      const width=s.right-s.left;
      box(s.key+'_front_rail',[(s.left+s.right)/2,s.railY,layout.frontZ],[width,.16,.14],wood);
      box(s.key+'_back_rail',[(s.left+s.right)/2,s.railY+.3,layout.backZ],[width,.16,.14],wood);
      const roof=box(s.key+'_roof',[(s.left+s.right)/2,s.railY+.23,(layout.frontZ+layout.backZ)/2],
        [width,.025,Math.hypot(layout.backZ-layout.frontZ,.3)],cloth);
      roof.rotation.x=Math.atan2(.3,layout.frontZ-layout.backZ);
      const bottom=Math.min(...s.feet.map(f=>feet.find(p=>p.x===f.x)!.y))+.12;
      const gap=.18,panel=(width-gap*(s.panels-1))/s.panels;
      for(let i=0;i<s.panels;i++)box(s.key+'_cloth_'+i,
        [s.left+i*(panel+gap)+panel/2,(bottom+s.railY+.3)/2,layout.backZ],
        [panel,s.railY+.3-bottom,.025],cloth);
    }
  }

  private validate(candidate:InstantiatedModel):AbstractMesh[] {
    if(!candidate.rootNodes.length)throw new Error('Missing shelter source root');
    const geometry=candidate.meshes.filter(m=>m.getTotalVertices()>0);
    const clean=(m:AbstractMesh)=>m.name.replace(/^(Clone of )+/,'');
    if(geometry.length!==required.length||required.some(name=>geometry.filter(m=>clean(m)===name).length!==1))
      throw new Error('Incomplete/duplicate shelter parts');
    for(const mesh of geometry){
      if(!mesh.isEnabled()||!mesh.isVisible||mesh.visibility!==1||!mesh.material||mesh.material.alpha!==1
        ||mesh.material.needAlphaBlendingForMesh(mesh)||mesh.material.needAlphaTestingForMesh(mesh))
        throw new Error('Shelter source must be complete and opaque');
      mesh.computeWorldMatrix(true);const b=mesh.getBoundingInfo().boundingBox;
      if(![...b.minimumWorld.asArray(),...b.maximumWorld.asArray()].every(Number.isFinite)||b.maximumWorld.z>=-.45)
        throw new Error('Shelter source enters cat corridor');
    }
    feet.forEach((f,i)=>{
      const b=geometry.find(m=>clean(m)==='CanopyShade_foot_'+i)!.getBoundingInfo().boundingBox;
      const min=[f.x-layout.footSize[0]/2,f.y,layout.postZ-layout.footSize[2]/2];
      const max=[f.x+layout.footSize[0]/2,f.y+layout.footSize[1],layout.postZ+layout.footSize[2]/2];
      if(min.some((v,k)=>Math.abs(v-b.minimumWorld.asArray()[k])>1e-5)
        ||max.some((v,k)=>Math.abs(v-b.maximumWorld.asArray()[k])>1e-5))
        throw new Error('Shelter foot misses its existing deck');
    });
    return geometry;
  }

  private async load():Promise<boolean> {
    let candidate:InstantiatedModel|null=null;
    try{
      candidate=await getAssetManager().instantiateSharedModel(this.scene,
        '/assets/models/environment/canopy_shadow_shelter.glb');
      if(this.retired||this.scene.isDisposed){candidate.dispose();return false;}
      const geometry=this.validate(candidate);
      for(const mesh of geometry){mesh.isPickable=false;mesh.receiveShadows=true;tagAs(mesh,'modular-final');}
      for(const mesh of this.casters)this.shadows.removeShadowCaster(mesh,false);
      this.casters=geometry;
      for(const mesh of this.casters)this.shadows.addShadowCaster(mesh,false);
      freezeStaticMeshes(candidate.meshes,{freezeMaterials:false});
      this.source=candidate;this.fallbackRoot.setEnabled(false);return true;
    }catch(error){
      candidate?.dispose();
      if(!this.retired&&!this.scene.isDisposed)console.warn('[Canopy] Shade source unavailable; retaining supported cloth:',error);
      return false;
    }
  }

  dispose():void {
    if(this.retired)return;this.retired=true;
    this.scene.onDisposeObservable.remove(this.observer);
    for(const mesh of this.casters)this.shadows.removeShadowCaster(mesh,false);
    this.casters=[];this.source?.dispose();this.source=null;
    this.fallbackRoot.dispose();for(const material of this.materials)material.dispose();
  }
}
